<template>
  <div class="vi-root">
    <LoadingOverlay v-if="!rendered" />

    <div v-else>
      <!-- HEADER -->
      <div class="vi-header">
        <div class="vi-header-left">
          <div class="vi-header-icon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
            >
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div>
            <h1 class="vi-title">Insights</h1>
            <p class="vi-sub">Análise de consumos e segmentação de clientes</p>
          </div>
        </div>
      </div>

      <!-- FILTROS -->
      <insights-filtros
        :filtro-grupo="filtroSelecionado"
        :filtros="filtros_utilizador"
        :restaurantes="restaurantes"
        :filtro-consumo="filtroConsumo"
        @mudar-grupo="mudarGrupo"
        @update:restaurantes="filtroConsumo.restaurantes = $event"
        @update:dataInicio="filtroConsumo.dataInicio = $event"
        @update:dataFim="filtroConsumo.dataFim = $event"
        @filtrar="aplicarFiltroDatas"
        @limpar="limparDatas"
      />

      <!-- KPIs + GRÁFICOS PIZZA -->
      <insights-kpis
        :total-visitas="totalVisitas"
        :total-vendas="totalVendas"
        :ticket-medio="ticketMedio"
        :visitas-assiduo="visitasAssiduo"
        :visitas-frequente="visitasFrequente"
        :visitas-ocasional="visitasOcasional"
        :definicoes="dadosDefinicoes"
        :pie-restaurantes="pieRestaurantes"
        :pie-refeicoes="pieRefeicoes"
        :pie-faixa-etaria="pieFaixaEtaria"
        :pie-genero="pieGenero"
      />

      <!-- TOP PRODUTOS POR CATEGORIA -->
      <insights-consumos :consumos="listAllConsumos" />
    </div>
  </div>
</template>

<script>
  import LoadingOverlay from "@/components/Loading/LoadingOverlay";
  import { filtrosService, definicoesService, cartaoService } from "@/services";
  import Chart from "chart.js";
  import ChartDataLabels from "chartjs-plugin-datalabels";

  import InsightsFiltros from "./InsightsFiltros.vue";
  import InsightsKpis from "./InsightsKpis.vue";
  import InsightsConsumos from "./InsightsConsumos.vue";

  Chart.plugins.register(ChartDataLabels);

  const PIE_EXTRA_OPTIONS = {
    responsive: true,
    legend: {
      display: true,
      position: "bottom",
      labels: {
        usePointStyle: true,
        padding: 20,
        fontColor: "#333",
        fontSize: 12,
      },
    },
    tooltips: {
      callbacks: {
        label(tooltipItem, data) {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const label = data.labels[tooltipItem.index] || "";
          const gasto = dataset.data[tooltipItem.index].toFixed(2);
          const visitas = dataset.meta?.visitas?.[tooltipItem.index] ?? "";
          return `${label}: ${gasto}€ | ${visitas} visitas`;
        },
      },
    },
    animation: { animateScale: true, animateRotate: true },
    plugins: {
      datalabels: {
        color: "#fff",
        font: { weight: "bold" },
        formatter(value, ctx) {
          if (!value) return "";
          const ds = ctx.chart?.data?.datasets?.[0];
          if (!ds?.data) return "";
          const total = ds.data.reduce((a, b) => a + b, 0);
          const perc = total ? ((value / total) * 100).toFixed(1) : 0;
          return perc > 0 ? perc + "%" : "";
        },
      },
    },
  };

  const PALETTE = ["#FF5D2C", "#FF825D", "#FFA570", "#FF931F", "#FFAD33"];
  const FALLBACK_COLOR = "#FEBE5F";

  function buildPie(array, labelKey, gastoKey, visitasKey, label) {
    const top5 = array.slice(0, 5);
    const outros = array.slice(5);
    if (outros.length) {
      top5.push({
        [labelKey]: "Outros",
        [gastoKey]: outros.reduce((s, i) => s + i[gastoKey], 0),
        [visitasKey]: outros.reduce((s, i) => s + i[visitasKey], 0),
      });
    }
    return {
      chartData: {
        labels: top5.map((i) => i[labelKey]),
        datasets: [
          {
            data: top5.map((i) => i[gastoKey]),
            backgroundColor: top5.map((_, idx) =>
              idx < PALETTE.length ? PALETTE[idx] : FALLBACK_COLOR,
            ),
            label,
            meta: { visitas: top5.map((i) => i[visitasKey]) },
          },
        ],
      },
      extraOptions: { ...PIE_EXTRA_OPTIONS },
    };
  }

  export default {
    components: {
      LoadingOverlay,
      InsightsFiltros,
      InsightsKpis,
      InsightsConsumos,
    },

    data() {
      return {
        rendered: false,
        filtros_utilizador: [],
        filtroSelecionado: null,
        filtroAtivo: null,
        restaurantes: [],
        filtroConsumo: { restaurantes: [], dataInicio: null, dataFim: null },

        tableData: [],
        tableDataOriginal: [],
        tableDataGlobais: [],

        // KPIs
        totalVisitas: 0,
        totalVendas: 0,
        ticketMedio: 0,
        visitasAssiduo: 0,
        visitasFrequente: 0,
        visitasOcasional: 0,
        dadosDefinicoes: {},

        // Consumos por categoria
        listAllConsumos: {},

        // Pies
        pieGenero: {
          chartData: { labels: [], datasets: [{ data: [] }] },
          extraOptions: {},
        },
        pieFaixaEtaria: {
          chartData: { labels: [], datasets: [{ data: [] }] },
          extraOptions: {},
        },
        pieRestaurantes: {
          chartData: { labels: [], datasets: [{ data: [] }] },
          extraOptions: {},
        },
        pieRefeicoes: {
          chartData: { labels: [], datasets: [] },
          extraOptions: {},
        },
      };
    },

    methods: {
      // ── Helpers ──────────────────────────────────────────────────────
      calcularIdade(dataNascimento) {
        const hoje = new Date();
        const nasc = new Date(dataNascimento);
        let idade = hoje.getFullYear() - nasc.getFullYear();
        if (
          hoje.getMonth() < nasc.getMonth() ||
          (hoje.getMonth() === nasc.getMonth() &&
            hoje.getDate() < nasc.getDate())
        )
          idade--;
        return idade;
      },
      faixaEtaria(idade) {
        if (idade == null) return "–";
        if (idade < 18) return "<18";
        if (idade <= 24) return "18–24";
        if (idade <= 34) return "25–34";
        if (idade <= 44) return "35–44";
        if (idade <= 54) return "45–54";
        return "55+";
      },

      // ── Dados ─────────────────────────────────────────────────────────
      async carregarFiltros() {
        try {
          this.filtros_utilizador = await filtrosService.listar();
        } catch (err) {
          console.error("Erro ao carregar filtros:", err);
        }
      },
      async carregarRestaurantes() {
        try {
          const rests = await filtrosService.getRestaurantes();
          this.restaurantes = rests.map((r) => ({
            value: r.nome,
            label: r.nome,
          }));
        } catch (err) {
          console.error("Erro ao carregar restaurantes:", err);
        }
      },

      limparDatas() {
        this.filtroConsumo.dataInicio = null;
        this.filtroConsumo.dataFim = null;
        this.getCartoes();
      },
      aplicarFiltroDatas() {
        this.getCartoes(
          this.filtroConsumo.dataInicio,
          this.filtroConsumo.dataFim,
        );
      },

      async mudarGrupo(grupo) {
        this.rendered = false;
        this.filtroAtivo = grupo;
        this.filtroSelecionado = grupo;

        if (grupo === "0" || grupo === null) {
          this.filtroAtivo = null;
          this.filtroSelecionado = null;
        }
        await this.getCartoes();
        this.rendered = true;
      },

      async getCartoes(dataInicioArg = null, dataFimArg = null) {
        this.rendered = false;
        try {
          const hoje = new Date();
          const seisMesesAtras = new Date();
          seisMesesAtras.setMonth(hoje.getMonth() - 6);

          const dataInicioEnviar =
            dataInicioArg ||
            this.filtroConsumo.dataInicio ||
            seisMesesAtras.toISOString().slice(0, 10);
          const dataFimEnviar =
            dataFimArg ||
            this.filtroConsumo.dataFim ||
            hoje.toISOString().slice(0, 10);

          const filtroId = this.filtroSelecionado || this.filtroAtivo || null;

          const response = await cartaoService.getInsights({
            filtroId,
            dataInicio: dataInicioEnviar,
            dataFim: dataFimEnviar,
          });

          const { dataInicio, dataFim, clientes } = response;
          this.filtroConsumo.dataInicio = dataInicio;
          this.filtroConsumo.dataFim = dataFim;

          const enriquecidos = clientes.map((c) => ({
            ...c,
            faixa_etaria: this.faixaEtaria(
              this.calcularIdade(c.data_nascimento),
            ),
          }));

          this.tableData = enriquecidos;
          this.tableDataOriginal = JSON.parse(JSON.stringify(enriquecidos));
          this.tableDataGlobais = JSON.parse(JSON.stringify(enriquecidos));

          await this.gerarInsights(enriquecidos);
          await this.aplicarFiltroConsumo();
        } catch (err) {
          console.error("❌ Erro Insights:", err);
        }
        setTimeout(() => {
          this.rendered = true;
        }, 300);
      },

      // ── Filtro local (restaurante + datas) ────────────────────────────
      aplicarFiltroConsumo() {
        let resultados = JSON.parse(JSON.stringify(this.tableDataGlobais));

        if (this.filtroConsumo.restaurantes.length) {
          resultados = resultados
            .map((c) => ({
              ...c,
              visitas: (c.visitas || []).filter((v) =>
                this.filtroConsumo.restaurantes.includes(v.restaurante),
              ),
            }))
            .filter((c) => c.visitas.length > 0);
        }

        if (this.filtroConsumo.dataInicio || this.filtroConsumo.dataFim) {
          const inicio = this.filtroConsumo.dataInicio || null;
          const fim = this.filtroConsumo.dataFim || null;
          resultados = resultados
            .map((c) => ({
              ...c,
              visitas: (c.visitas || []).filter((v) => {
                const d = (v.data_visita || "").slice(0, 10);
                if (inicio && d < inicio) return false;
                if (fim && d > fim) return false;
                return true;
              }),
            }))
            .filter((c) => c.visitas.length > 0);
        }

        // recalcular agregados
        resultados = resultados.map((c) => {
          const totalVisitas = c.visitas.length;
          const gastoTotal = c.visitas.reduce(
            (s, v) => s + (v.total_pago || 0),
            0,
          );
          const consumos = [];
          c.visitas.forEach((v) => {
            (v.consumos || []).forEach((cc) => {
              let ex = consumos.find((p) => p.produto === cc.produto);
              if (!ex) {
                ex = {
                  produto: cc.produto,
                  categoria: cc.categoria,
                  total_quantidade: 0,
                  total_pago: 0,
                };
                consumos.push(ex);
              }
              ex.total_quantidade += cc.quantidade || 0;
              ex.total_pago += cc.pago || 0;
            });
          });
          const restCount = {};
          c.visitas.forEach((v) => {
            restCount[v.restaurante] = (restCount[v.restaurante] || 0) + 1;
          });
          const top_restaurante = c.visitas.length
            ? Object.keys(restCount).reduce((a, b) =>
                restCount[a] > restCount[b] ? a : b,
              )
            : null;
          return {
            ...c,
            visitas_total: totalVisitas,
            gasto_total: gastoTotal,
            ticket_medio: totalVisitas > 0 ? gastoTotal / totalVisitas : 0,
            top_restaurante,
            consumos_comida: consumos,
          };
        });

        this.tableData = resultados;
        this.gerarInsights(resultados);
      },

      // ── Classificar segmentos ─────────────────────────────────────────
      classificarClientes(clientes, def) {
        const hoje = new Date();
        const limiteDias = Math.max(
          def["assiduo_dias"] || 30,
          def["frequente_dias"] || 90,
          def["ocasional_dias"] || 180,
        );
        let assiduos = 0,
          frequentes = 0,
          ocasionais = 0;

        for (const c of clientes) {
          const visitas = (c.visitas || []).filter((v) => {
            const diff =
              (hoje - new Date(v.data_visita)) / (1000 * 60 * 60 * 24);
            return diff <= limiteDias;
          }).length;

          if (visitas >= (def["assiduo_visitas"] || 6)) assiduos++;
          else if (visitas >= (def["frequente_visitas"] || 3)) frequentes++;
          else if (visitas >= (def["ocasional_visitas"] || 1)) ocasionais++;
        }
        return {
          clientes_assiduos: assiduos,
          clientes_frequentes: frequentes,
          clientes_ocasionais: ocasionais,
        };
      },

      // ── Gerar todos os insights ───────────────────────────────────────
      async gerarInsights(clientes) {
        const defsData = await definicoesService.get();
        const definicoes = {};
        defsData.forEach((d) => {
          definicoes[d.opt_key] = parseInt(d.value, 10);
        });

        this.dadosDefinicoes = {
          limiteAssiduoVisitas: definicoes["assiduo_visitas"] || 6,
          limiteAssiduoDias: definicoes["assiduo_dias"] || 30,
          limiteFrequenteVisitas: definicoes["frequente_visitas"] || 3,
          limiteFrequenteDias: definicoes["frequente_dias"] || 90,
          limiteOcasionalVisitas: definicoes["ocasional_visitas"] || 1,
          limiteOcasionalDias: definicoes["ocasional_dias"] || 180,
        };

        const { clientes_assiduos, clientes_frequentes, clientes_ocasionais } =
          this.classificarClientes(clientes, definicoes);

        this.visitasAssiduo = clientes_assiduos;
        this.visitasFrequente = clientes_frequentes;
        this.visitasOcasional = clientes_ocasionais;
        this.totalVisitas = clientes.reduce(
          (s, c) => s + (c.visitas_total || 0),
          0,
        );
        this.totalVendas = clientes.reduce(
          (s, c) => s + (c.gasto_total || 0),
          0,
        );
        this.ticketMedio =
          this.totalVisitas > 0 ? this.totalVendas / this.totalVisitas : 0;

        // ── Top produtos por categoria ────────────────────────────────
        const categorias = {};
        clientes.forEach((c) => {
          (c.consumos_comida || []).forEach((consumo) => {
            const { categoria, produto, total_quantidade, total_pago } =
              consumo;
            if (!categorias[categoria]) categorias[categoria] = {};
            if (!categorias[categoria][produto])
              categorias[categoria][produto] = {
                name: produto,
                unidades_vendidas: 0,
                total_pago: 0,
              };
            categorias[categoria][produto].unidades_vendidas +=
              total_quantidade;
            categorias[categoria][produto].total_pago += total_pago;
          });
        });
        const result = {};
        for (const cat in categorias) {
          const items = Object.values(categorias[cat]);
          const totalUn = items.reduce((s, i) => s + i.unidades_vendidas, 0);
          result[cat] = items
            .map((i) => ({
              ...i,
              progress: ((i.unidades_vendidas / totalUn) * 100).toFixed(2),
              color: "orange",
            }))
            .sort((a, b) => b.unidades_vendidas - a.unidades_vendidas);
        }
        this.listAllConsumos = result;

        // ── Pies ──────────────────────────────────────────────────────
        // Faixa etária
        const faixaMap = {};
        clientes.forEach((c) => {
          const f = c.faixa_etaria || "Não definido";
          faixaMap[f] = faixaMap[f] || { label: f, gasto: 0, visitas: 0 };
          faixaMap[f].gasto += c.gasto_total || 0;
          faixaMap[f].visitas += c.visitas_total || 0;
        });
        this.pieFaixaEtaria = buildPie(
          Object.values(faixaMap).sort((a, b) => b.gasto - a.gasto),
          "label",
          "gasto",
          "visitas",
          "Gasto por Faixa Etária (€)",
        );

        // Género
        const generoMap = {};
        clientes.forEach((c) => {
          const g = c.sexo || "Não definido";
          generoMap[g] = generoMap[g] || { label: g, gasto: 0, visitas: 0 };
          generoMap[g].gasto += c.gasto_total || 0;
          generoMap[g].visitas += c.visitas_total || 0;
        });
        this.pieGenero = buildPie(
          Object.values(generoMap).sort((a, b) => b.gasto - a.gasto),
          "label",
          "gasto",
          "visitas",
          "Gasto por Género (€)",
        );

        // Restaurante
        const restMap = {};
        clientes.forEach((c) => {
          const r = c.top_restaurante || "Não definido";
          restMap[r] = restMap[r] || { label: r, gasto: 0, visitas: 0 };
          restMap[r].gasto += c.gasto_total || 0;
          restMap[r].visitas += c.visitas_total || 0;
        });
        this.pieRestaurantes = buildPie(
          Object.values(restMap).sort((a, b) => b.gasto - a.gasto),
          "label",
          "gasto",
          "visitas",
          "Gasto por Restaurante (€)",
        );

        // Refeição (período)
        const refMap = {};
        clientes.forEach((c) => {
          (c.visitas || []).forEach((v) => {
            const r = v.refeicao || "Outro";
            refMap[r] = refMap[r] || { label: r, gasto: 0, visitas: 0 };
            refMap[r].gasto += v.total_pago || 0;
            refMap[r].visitas += 1;
          });
        });
        this.pieRefeicoes = buildPie(
          Object.values(refMap).sort((a, b) => b.gasto - a.gasto),
          "label",
          "gasto",
          "visitas",
          "Gasto por Refeição (€)",
        );
      },
    },

    async mounted() {
      await this.carregarFiltros();
      await this.carregarRestaurantes();

      const grupoURL = this.$route.query.grupo || null;
      if (grupoURL) {
        this.filtroSelecionado = grupoURL;
        this.filtroAtivo = grupoURL;
      }

      await this.getCartoes();
      this.rendered = true;
    },
  };
</script>

<style scoped>
  .vi-root {
    padding: 28px 28px 48px;
    font-family: "DM Sans", "Outfit", system-ui, sans-serif;
    min-height: 100vh;
    background: #f8f6f3;
  }
  .vi-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
  }
  .vi-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .vi-header-icon {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    background: #ff825d;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 4px 14px rgba(255, 130, 93, 0.3);
  }
  .vi-title {
    font-size: 20px;
    font-weight: 800;
    color: #1a1210;
    margin: 0;
    letter-spacing: -0.4px;
  }
  .vi-sub {
    font-size: 13px;
    color: #b0a8a0;
    margin: 3px 0 0;
  }
</style>
